import { useMemo } from 'react'

import { useFeatureValue } from '@growthbook/growthbook-react'
import { FEATURE_KEYS } from '~/shared/constants/commonConstants'
import { PageId } from '~/shared/constants/pagesConstants'
import { usePageInfoContext } from '~/shared/contexts/PageInfoContext'
import { useBulkSelector } from '~/shared/hooks/useBulkSelector'
import {
  selectCurrentVariantSteps,
  selectHasUpsellOffer,
} from '~/shared/store/common/selects'
import {
  selectHasAccessToAppByEmail,
  selectUserCountryCode,
  selectUserStatus,
} from '~/shared/store/user/selects'

const EMAIL_PAGE_IDS = new Set<PageId>([PageId.EMAIL_1])
const US_COUNTRY_CODE = 'US'
const UPCELL_TEST_VARIANTS = new Set(['recurring-us', 'one-time-us'])

const useSelectors = () =>
  useBulkSelector({
    steps: selectCurrentVariantSteps,
    userStatus: selectUserStatus,
    hasUpsellOffer: selectHasUpsellOffer,
    hasAccessToApp: selectHasAccessToAppByEmail,
    countryCode: selectUserCountryCode,
  })

export const useAvailableFunnelRoute = (): string => {
  const { steps, hasUpsellOffer, userStatus, hasAccessToApp, countryCode } =
    useSelectors()

  const { currentUpsellPageId, currentPageId } = usePageInfoContext()

  const loginBeforeUpsellVariant = useFeatureValue(
    FEATURE_KEYS.login_before_upsell,
    'control',
  )
  const shouldLoginBeforeUpsell = loginBeforeUpsellVariant === 'test'

  const upcellVariant = useFeatureValue(FEATURE_KEYS.upcell, 'control')
  const isUpcellTestVariant = UPCELL_TEST_VARIANTS.has(upcellVariant)

  const effectiveHasAccessToApp =
    hasAccessToApp !== null ? hasAccessToApp : userStatus?.hasSubscription

  const isUpsellAvailable =
    hasUpsellOffer &&
    userStatus?.upsell.isUpsellAvailable &&
    !userStatus?.upsell.isUpsellPurchased &&
    !userStatus?.config.targetUuid &&
    !effectiveHasAccessToApp &&
    (countryCode !== US_COUNTRY_CODE || isUpcellTestVariant)

  return useMemo(() => {
    if (!userStatus) return ''

    // If user already has access to the app (account/subscription), skip to download.
    // But if they don't have an account yet, route through login first.
    if (
      (userStatus?.account.hasAccount || userStatus?.hasSubscription) &&
      !isUpsellAvailable
    ) {
      if (!userStatus?.account.hasAccount) {
        return PageId.LOGIN
      }
      return PageId.DOWNLOAD
    }

    // Upsell path (if it exists) is still pinned to the dedicated upsell route.
    // For the login-before-upsell test variant, route through login first;
    // once the account exists, fall through to the upsell route as usual.
    if (userStatus?.hasSubscription && isUpsellAvailable) {
      if (shouldLoginBeforeUpsell && !userStatus?.account.hasAccount) {
        return PageId.LOGIN
      }
      return currentUpsellPageId
    }

    // After email is saved:
    // - forbid going back to `email-1`
    // - otherwise do NOT pin to any particular subscription route
    //   (so new cohorts won't require updating this hook).
    if (userStatus?.email.hasEmail) {
      // todo: review and find better way to handle with email
      if (!EMAIL_PAGE_IDS.has(currentPageId)) return ''

      const emailStepIdx = steps.findIndex((step) => step.id === currentPageId)
      return emailStepIdx >= 0 ? steps[emailStepIdx + 1]?.id ?? '' : ''
    }

    return ''
  }, [
    userStatus,
    steps,
    currentUpsellPageId,
    currentPageId,
    isUpsellAvailable,
    shouldLoginBeforeUpsell,
  ])
}
